/**
 * useNotificationWebSocket
 * ========================
 * Replaces the 30-second polling mechanism in NotificationsDropdown with a
 * persistent WebSocket connection that receives `notification.created` events
 * in real time.
 *
 * Implements §5.2 Phase 2, Task 2.1 of the v3.4.5 implementation plan.
 *
 * Usage:
 *   useNotificationWebSocket((notification) => {
 *     // handle incoming notification
 *   });
 */

import { useEffect, useCallback, useRef } from 'react';

export interface NotificationData {
  id: number;
  notification_type: string;
  title: string;
  message: string;
  read: boolean;
  created_at: string;
  data: Record<string, any>;
}

interface NotificationEvent {
  type: 'notification.created';
  data: NotificationData;
}

// REACT_APP_WS_URL already includes the full ws base path (e.g. ws://localhost:8001/api/v1/ws).
// Fall back to deriving it from REACT_APP_API_URL if WS_URL is not set.
const WS_BASE: string =
  process.env.REACT_APP_WS_URL ||
  (process.env.REACT_APP_API_URL || window.location.origin).replace(/^http/, "ws");

/** Show a browser push notification if permission is granted. */
function showBrowserNotification(n: NotificationData): void {
  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification(n.title, {
      body: n.message,
      icon: '/logo192.png',
      tag: `notification-${n.id}`,
    });
  }
}

export function useNotificationWebSocket(
  onNotification: (notification: NotificationData) => void
): void {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Keep a stable ref to the callback so reconnects pick up the latest version
  const callbackRef = useRef(onNotification);
  callbackRef.current = onNotification;

  const connect = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const url = `${WS_BASE}/notifications?token=${token}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      console.debug('[NotificationWS] Connected');
    };

    ws.onmessage = (event: MessageEvent) => {
      try {
        const payload: NotificationEvent = JSON.parse(event.data);
        if (payload.type === 'notification.created') {
          callbackRef.current(payload.data);
          showBrowserNotification(payload.data);
        }
      } catch {
        // Malformed frame – ignore
      }
    };

    ws.onclose = () => {
      console.debug('[NotificationWS] Disconnected – reconnecting in 5 s');
      reconnectRef.current = setTimeout(connect, 5_000);
    };

    ws.onerror = () => {
      ws.close(); // triggers onclose → reconnect
    };
  }, []); // stable – relies on callbackRef for the handler

  useEffect(() => {
    connect();
    return () => {
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      wsRef.current?.close();
    };
  }, [connect]);
}
